import * as React from "react";
import { useParams } from "react-router-dom";

import { Loading } from "../../../../../../../react-components/lu-component/src/index";
import type { AccountWithoutPassword } from "../../../../../../../server/models/account";
import { adCopyV3Endpoint } from "../../../../../../../server/router/ad-copy/endpoint";
import type { GetAdCopySimulationV3Response } from "../../../../../../../server/types/request/ad-copy/report/v3/simulation";
import { ConfirmBox } from "../../../../../components/parts/confirm-box";
import { get, makeDwnloadImage, makeError, makeErrorBlob, put, remove } from "../../../../../lib/request";
import { initState, reducer } from "../../../../../reducers/ad-copy/report/latest/simulation";
import {
	AdCopySimulationLatestPage,
	AdCopySimulationPageProps,
} from "../../../../../components/pages/ad-copy/report/latest/simulation";
import type {
	UpsertAdCopyCorrectionValueRequest,
	UpsertAdCopyCP2Response,
} from "../../../../../../../server/types/request/ad-copy/report/cp2";
import { isDefined } from "../../../../../lib/data";

export const AdCopySimulationContainer = (loginAccount: AccountWithoutPassword) => {
	const [state, dispatch] = React.useReducer(reducer, initState);

	const { info, loading, ...sProps } = state;

	const { jobId } = useParams<{
		jobId: string;
	}>();

	const refetch = React.useCallback(() => {
		get<GetAdCopySimulationV3Response>(`${adCopyV3Endpoint.simulation}/${jobId}`)
			.then((response) => {
				dispatch({ payload: response.data, type: "loadData" });
			})
			.catch((error) => {
				dispatch({ payload: makeError(error), type: "changeMessageInfo" });
			});
	}, [jobId]);

	React.useEffect(() => refetch(), [refetch]);

	const onChangeRatio = React.useCallback<AdCopySimulationPageProps["onChangeRatio"]>((target, name, value) => {
		dispatch({ payload: { name, target, value }, type: "changeRatio" });
	}, []);

	const onSimulation = React.useCallback<AdCopySimulationPageProps["onSimulation"]>(
		(conceptType) => {
			const html = document.getElementsByClassName("simulation-container").item(0)?.outerHTML;

			dispatch({ payload: true, type: "changeLoading" });
			const correctionValue = sProps.simulationPredictionData[conceptType]?.correctionValue;

			makeDwnloadImage(
				`${adCopyV3Endpoint.simulation}/${jobId}`,
				{ ...correctionValue, html },
				`${sProps.job.jobNum}_executive_summary.png`,
			)
				.then(() => dispatch({ payload: true, type: "downloadSimulation" }))
				.catch(async (error) => {
					dispatch({ payload: await makeErrorBlob(error, true), type: "changeMessageInfo" });
				});
		},
		[sProps.simulationPredictionData, sProps.job?.jobNum, jobId],
	);

	const onSubmitPurchaseRate = React.useCallback<AdCopySimulationPageProps["onSubmitPurchaseRate"]>(() => {
		const cp = sProps.currentProduct?.correctionValue;
		if (!isDefined(cp)) return;
		const { purchaseRate } = cp;
		dispatch({ type: "changeLoading", payload: true });
		put<UpsertAdCopyCP2Response>(`${adCopyV3Endpoint.cpPurchaseRate}/${jobId}`, purchaseRate)
			.then((response) => dispatch({ type: "updateData", payload: response.data }))
			.catch((error) => {
				dispatch({ type: "changeMessageInfo", payload: makeError(error, true) });
			});
	}, [jobId, sProps.currentProduct?.correctionValue]);

	const onSubmitRecognitionRate = React.useCallback<AdCopySimulationPageProps["onSubmitRecognitionRate"]>(() => {
		const cp = sProps.currentProduct?.correctionValue;
		if (!isDefined(cp)) return;
		const { recognitionRate } = cp;
		dispatch({ type: "changeLoading", payload: true });
		put<UpsertAdCopyCP2Response>(`${adCopyV3Endpoint.cpRecognitionRate}/${jobId}`, recognitionRate)
			.then((response) => dispatch({ type: "updateData", payload: response.data }))
			.catch((error) => {
				dispatch({ type: "changeMessageInfo", payload: makeError(error, true) });
			});
	}, [jobId, sProps.currentProduct?.correctionValue]);

	const onChangeCP = React.useCallback<AdCopySimulationPageProps["onChangeCP"]>((name, value) => {
		dispatch({ payload: { name, value }, type: "changeCP" });
	}, []);

	const onClearCP = React.useCallback(() => {
		remove<GetAdCopySimulationV3Response>(`${adCopyV3Endpoint.cp}/${jobId}`)
			.then((response) => dispatch({ payload: response.data, type: "clearCPData" }))
			.catch((error) => {
				dispatch({ payload: makeError(error, true), type: "changeMessageInfo" });
			});
	}, [jobId]);

	const onSimulationSave = React.useCallback<AdCopySimulationPageProps["onSimulationSave"]>(
		(conceptType) => {
			dispatch({ payload: true, type: "changeLoading" });
			const correctionValue: UpsertAdCopyCorrectionValueRequest =
				sProps.simulationPredictionData[conceptType]?.correctionValue;

			put(`${adCopyV3Endpoint.simulation}/${jobId}`, correctionValue)
				.then(() => dispatch({ payload: { isSuccess: true, message: "保存が完了しました。" }, type: "saveSimulation" }))
				.catch((error) => {
					dispatch({ payload: makeError(error, true), type: "saveSimulation" });
				});
		},
		[sProps.simulationPredictionData, jobId],
	);

	const setChanged = React.useCallback((changed: boolean) => dispatch({ payload: changed, type: "setChanged" }), []);

	return (
		<>
			<Loading loading={loading} />

			<ConfirmBox info={info} titleLabel="保存処理" />

			{sProps.activity && sProps.evaluation && sProps.job && sProps.constant && sProps.referenceSourceData && (
				<AdCopySimulationLatestPage
					{...sProps}
					activity={sProps.activity}
					evaluation={sProps.evaluation}
					constant={sProps.constant}
					job={sProps.job}
					referenceSourceData={sProps.referenceSourceData}
					errors={info.errors}
					loginAccount={loginAccount}
					onChangeCP={onChangeCP}
					onChangeRatio={onChangeRatio}
					onClearCP={onClearCP}
					onSimulation={onSimulation}
					onSimulationSave={onSimulationSave}
					onSubmitPurchaseRate={onSubmitPurchaseRate}
					onSubmitRecognitionRate={onSubmitRecognitionRate}
					refetch={refetch}
					setChanged={setChanged}
				/>
			)}
		</>
	);
};
